import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type DialogSize = 'small' | 'normal' | 'big';

export interface CuiDialogOpenOptions<D = unknown> extends Omit<
  DialogConfig<D>,
  'ariaModal' | 'autoFocus' | 'backdropClass' | 'panelClass' | 'restoreFocus'
> {
  size?: DialogSize;
}

export interface CuiDialogRef<R = unknown> {
  result: Promise<R | undefined>;
  close(result?: R): void;
}

@Injectable({ providedIn: 'root' })
export class CuiDialogService {
  private readonly dialog = inject(Dialog);

  open<R = unknown, D = unknown, C = unknown>(
    component: ComponentType<C>,
    options: CuiDialogOpenOptions<D> = {}
  ): CuiDialogRef<R> {
    const { size = 'normal', ...config } = options;
    const dialogRef = this.dialog.open<R, D, C>(
      component,
      this.createConfig<D, R, C>(size, config)
    );

    return {
      result: firstValueFrom(dialogRef.closed, { defaultValue: undefined }),
      close: (result?: R) => dialogRef.close(result),
    };
  }

  private createConfig<D, R, C>(
    size: DialogSize,
    config: DialogConfig<D>
  ): DialogConfig<D, DialogRef<R, C>> {
    return {
      ariaModal: true,
      autoFocus: 'first-tabbable',
      backdropClass: 'cui-dialog-backdrop',
      panelClass: ['cui-dialog-panel', `cui-dialog-panel--${size}`],
      restoreFocus: true,
      ...config,
    } as DialogConfig<D, DialogRef<R, C>>;
  }
}
